// Operator Governance surface: seven server-projected Markdown documents, immutable history, and editing of
// server-authorised company components only. There is no draft/review/approval workflow: Save creates the next
// active version immediately. The component owns temporary editor/view state, never governance business rules.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenBody, ScreenSubTabs, ScreenToolbar, TbInfo } from '../app/screen-chrome.tsx';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import type { ProjectionState } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { EmptyState, Panel } from './primitives.tsx';
import type {
  GovernanceDocumentKey,
  GovernanceDocumentSummary,
  GovernanceDocumentView,
  GovernanceListView,
  GovernanceVersionView,
} from './governance-projections.ts';
import {
  GovernanceEditor,
  GovernanceMarkdown,
  OwnershipLabel,
  ReadonlyGovernanceComponent,
} from './components/GovernanceMarkdown.tsx';
import { GovernanceHistory } from './components/GovernanceHistory.tsx';

type GovernanceViewMode = 'effective' | 'components' | 'history';

const GOVERNANCE_VIEW_TABS = [
  { id: 'effective', label: 'Effective' },
  { id: 'components', label: 'Components' },
] as const;

function GovernanceRail({
  documents,
  active,
  onSelect,
}: {
  readonly documents: readonly GovernanceDocumentSummary[];
  readonly active: GovernanceDocumentKey;
  readonly onSelect: (key: GovernanceDocumentKey) => void;
}): React.JSX.Element {
  return (
    <aside aria-label="Governance documents" className="space-y-2">
      {documents.map((document) => (
        <button
          key={document.documentKey}
          type="button"
          aria-current={active === document.documentKey ? 'page' : undefined}
          data-document-key={document.documentKey}
          onClick={() => onSelect(document.documentKey)}
          className="w-full rounded-lg border border-border bg-surface px-4 py-3 text-left hover:border-border-strong"
        >
          <span className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-sm font-semibold text-text">{document.filename}</span>
              <span className="mt-1 block text-xs text-text-subtle">
                {document.historyCount} {document.historyCount === 1 ? 'version' : 'versions'}
              </span>
            </span>
            <OwnershipLabel ownership={document.ownership} />
          </span>
        </button>
      ))}
    </aside>
  );
}

function ComponentsView({
  document,
  markdown,
  saving,
  saveNote,
  onMarkdownChange,
  onSave,
}: {
  readonly document: GovernanceDocumentView;
  readonly markdown: string;
  readonly saving: boolean;
  readonly saveNote: string | null;
  readonly onMarkdownChange: (markdown: string) => void;
  readonly onSave: () => void;
}): React.JSX.Element {
  return (
    <div className="space-y-5">
      {document.components.map((component) =>
        component.editable ? (
          <GovernanceEditor
            key={component.componentKey}
            component={component}
            markdown={markdown}
            saving={saving}
            saveNote={saveNote}
            onChange={onMarkdownChange}
            onSave={onSave}
          />
        ) : (
          <ReadonlyGovernanceComponent key={component.componentKey} component={component} />
        ),
      )}
    </div>
  );
}

function DocumentViews({
  document,
  mode,
  markdown,
  saving,
  saveNote,
  onMarkdownChange,
  onSave,
  onLoadHistory,
}: {
  readonly document: GovernanceDocumentView;
  readonly mode: GovernanceViewMode;
  readonly markdown: string;
  readonly saving: boolean;
  readonly saveNote: string | null;
  readonly onMarkdownChange: (markdown: string) => void;
  readonly onSave: () => void;
  readonly onLoadHistory: (version: GovernanceVersionView) => void;
}): React.JSX.Element {
  const api = useSurfaceApi();
  if (mode === 'effective') {
    return (
      <Panel className="min-h-96 px-6 py-5" dataMarker={{ name: 'view', value: 'effective' }}>
        <GovernanceMarkdown markdown={document.effectiveMarkdown} />
      </Panel>
    );
  }
  if (mode === 'history') {
    return (
      <GovernanceHistory
        documentKey={document.summary.documentKey}
        history={document.history}
        api={api}
        onLoadIntoEditor={onLoadHistory}
      />
    );
  }
  return (
    <ComponentsView
      document={document}
      markdown={markdown}
      saving={saving}
      saveNote={saveNote}
      onMarkdownChange={onMarkdownChange}
      onSave={onSave}
    />
  );
}

interface GovernanceDocumentPaneViewProps {
  readonly state: ProjectionState<GovernanceDocumentView>;
  readonly document: GovernanceDocumentView | null;
  readonly mode: GovernanceViewMode;
  readonly markdown: string;
  readonly saving: boolean;
  readonly saveNote: string | null;
  readonly onModeChange: (mode: GovernanceViewMode) => void;
  readonly onMarkdownChange: (markdown: string) => void;
  readonly onSave: () => void;
  readonly onLoadHistory: (version: GovernanceVersionView) => void;
}

function GovernanceDocumentHeader({
  document,
}: {
  readonly document: GovernanceDocumentView;
}): React.JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold text-text">{document.summary.title}</h2>
        <p className="mt-1 text-sm text-text-subtle">
          {document.summary.editable
            ? 'Operator-managed company content; each save creates an immutable version.'
            : 'Read-only here; this document is maintained centrally or generated.'}
        </p>
      </div>
      <OwnershipLabel ownership={document.summary.ownership} />
    </div>
  );
}

function GovernanceDocumentPaneView(props: GovernanceDocumentPaneViewProps): React.JSX.Element {
  const { state, document, mode } = props;
  const tabs = [
    ...GOVERNANCE_VIEW_TABS,
    { id: 'history', label: 'History', badge: document?.history.length ?? 0 },
  ];
  return (
    <div className="min-w-0">
      {document !== null ? <GovernanceDocumentHeader document={document} /> : null}
      <ScreenSubTabs
        label="Governance document views"
        tabs={tabs}
        active={mode}
        onSelect={(id) => {
          if (id === 'effective' || id === 'components' || id === 'history') props.onModeChange(id);
        }}
      />
      <div className="pt-5">
        <SurfaceFrame state={{ ...state, data: document }}>
          {(loaded) => (
            <DocumentViews
              document={loaded}
              mode={mode}
              markdown={props.markdown}
              saving={props.saving}
              saveNote={props.saveNote}
              onMarkdownChange={props.onMarkdownChange}
              onSave={props.onSave}
              onLoadHistory={props.onLoadHistory}
            />
          )}
        </SurfaceFrame>
      </div>
    </div>
  );
}

function useGovernanceDocumentPane(documentKey: GovernanceDocumentKey) {
  const api = useSurfaceApi();
  const load = useCallback(() => api.getGovernanceDocument(documentKey), [api, documentKey]);
  const state = useProjection<GovernanceDocumentView>(load);
  const [savedDocument, setSavedDocument] = useState<GovernanceDocumentView | null>(null);
  const document = savedDocument ?? state.data;
  const [mode, setMode] = useState<GovernanceViewMode>('effective');
  const [markdown, setMarkdown] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  useEffect(() => {
    setSavedDocument(null);
    setMode('effective');
    setSaveNote(null);
  }, [documentKey]);
  const editableComponent = document?.components.find((component) => component.editable);
  useEffect(() => {
    setMarkdown(editableComponent?.markdown ?? '');
  }, [editableComponent?.componentKey, editableComponent?.versionId, editableComponent?.markdown]);
  return {
    api,
    state,
    document,
    editableComponent,
    mode,
    markdown,
    saving,
    saveNote,
    setSavedDocument,
    setMode,
    setMarkdown,
    setSaving,
    setSaveNote,
  };
}

type GovernanceDocumentPaneState = ReturnType<typeof useGovernanceDocumentPane>;

async function saveGovernanceDocument(
  documentKey: GovernanceDocumentKey,
  pane: GovernanceDocumentPaneState,
  onSaved: () => void,
): Promise<void> {
  const { document, editableComponent } = pane;
  if (document === null || editableComponent === undefined || !document.summary.editable) return;
  pane.setSaving(true);
  pane.setSaveNote(null);
  const result = await pane.api.saveGovernanceVersion(documentKey, {
    markdown: pane.markdown,
    // Loading old history never replaces this base: saves still compare against CURRENT.
    expectedVersionId: document.summary.editableVersionId,
  });
  pane.setSaving(false);
  if (!result.ok) {
    pane.setSaveNote(
      result.code === 'conflict'
        ? 'A newer version exists. Reload this document before saving.'
        : result.note,
    );
    return;
  }
  pane.setSavedDocument(result.document);
  pane.setSaveNote('New version saved and active.');
  onSaved();
}

interface GovernanceDocumentPaneProps {
  readonly documentKey: GovernanceDocumentKey;
  readonly onSaved: () => void;
}

function GovernanceDocumentPane({
  documentKey,
  onSaved,
}: GovernanceDocumentPaneProps): React.JSX.Element {
  const pane = useGovernanceDocumentPane(documentKey);
  const loadHistory = (version: GovernanceVersionView): void => {
    pane.setMarkdown(version.markdown);
    pane.setSaveNote(
      `Version ${String(version.version)} loaded. Save to create a new active version.`,
    );
    pane.setMode('components');
  };
  return (
    <GovernanceDocumentPaneView
      state={pane.state}
      document={pane.document}
      mode={pane.mode}
      markdown={pane.markdown}
      saving={pane.saving}
      saveNote={pane.saveNote}
      onModeChange={pane.setMode}
      onMarkdownChange={(next) => {
        pane.setMarkdown(next);
        pane.setSaveNote(null);
      }}
      onSave={() => void saveGovernanceDocument(documentKey, pane, onSaved)}
      onLoadHistory={loadHistory}
    />
  );
}

function GovernanceBody({
  view,
  onSaved,
}: {
  readonly view: GovernanceListView;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const [selected, setSelected] = useState<GovernanceDocumentKey | null>(null);
  const active =
    view.documents.find((document) => document.documentKey === selected)?.documentKey ??
    view.documents[0]?.documentKey;
  if (active === undefined) {
    return <EmptyState>No governance documents are available for this deployment.</EmptyState>;
  }
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]" data-governance>
      <GovernanceRail documents={view.documents} active={active} onSelect={setSelected} />
      <GovernanceDocumentPane key={active} documentKey={active} onSaved={onSaved} />
    </div>
  );
}

/** Operator-only Governance administration surface. */
export function GovernanceSurface(): React.JSX.Element {
  const api = useSurfaceApi();
  const load = useMemo(() => api.getGovernanceDocuments, [api]);
  const state = useProjection<GovernanceListView>(load);
  return (
    <>
      <ScreenToolbar title="Governance">
        <TbInfo
          title="Governance"
          tip="The Markdown instructions Ritona carries into every enduring chat and task session. Company employees cannot edit this area."
        />
      </ScreenToolbar>
      <ScreenBody>
        <SurfaceFrame state={state}>
          {(view) => <GovernanceBody view={view} onSaved={state.reload} />}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}
